import { z } from 'zod';
import { objectIdSchema } from './objectId';
import { BasePaginationAndSearchSchema, PaginationResultSchema, SortBySchema } from './pagination';
import { PropertiesSchema } from './utils';

// Main ones
export enum GroupResourceType {
  FB = 'F&B',
  ENT = 'ENT',
  HSK = 'HSK',
}

export const GroupResourceTypeList = Object.values(GroupResourceType);
export const PhotographyGroupResourceTypeList = ['Photography', 'photography'];
export const AllResourceTypes = [...GroupResourceTypeList, ...PhotographyGroupResourceTypeList];

export const GroupResourceSchema = z.object({
  _id: objectIdSchema,
  resourceType: z.string(),
  price: z.number().min(0),
  name: z.string(),
  archived: z.boolean(),
  properties: PropertiesSchema.array(),
  source: objectIdSchema.optional(),
  // coerce makes the date pass the zod validation after it's sent from the API
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  createdBy: z.object({
    _id: objectIdSchema,
    userId: objectIdSchema,
    firstName: z.string(),
    lastName: z.string(),
  }),
  updatedBy: z.object({
    _id: objectIdSchema,
    userId: objectIdSchema,
    firstName: z.string(),
    lastName: z.string(),
  }),
});

export const GroupResourceBaseSchema = GroupResourceSchema.omit({
  _id: true,
}).extend({
  properties: z.string().array(),
  source: z.string().optional(),
});

export const UpdateGroupResourceSchema = GroupResourceSchema.extend({
  resourceType: z.string(),
}).partial();

export const CreateGroupResourceSchema = GroupResourceBaseSchema.omit({
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true,
}).extend({
  resourceType: z.string(),
});

const GroupResourceKeys = SortBySchema(GroupResourceSchema, ['properties', 'source', '_id']);
const GroupResourceFiltersSchema = z.object({
  createdAtStart: z.coerce.string().optional(),
  createdAtEnd: z.string().optional(),
  updatedAtStart: z.string().optional(),
  updatedAtEnd: z.string().optional(),
  userIds: z.array(z.string()).optional(),
  // cant use coerce with booleans bc false evaluates to true
  archived: z.string().transform(val => val === 'true'),
  type: z.string().array().optional(),
});
export const PaginateGroupResourcesParamsSchema = BasePaginationAndSearchSchema(
  GroupResourceKeys
).and(GroupResourceFiltersSchema);

export const GroupResourcePaginationResultSchema = PaginationResultSchema(GroupResourceSchema);
export const GroupResourceDuplicateSchema = GroupResourceSchema.pick({
  resourceType: true,
  price: true,
  name: true,
});

export type UpdateGroupResource = z.infer<typeof UpdateGroupResourceSchema>;
export type GroupResource = z.infer<typeof GroupResourceSchema>;
export type GroupResourceBase = z.infer<typeof GroupResourceBaseSchema>;
export type CreateGroupResource = z.infer<typeof CreateGroupResourceSchema>;
export type PaginateGroupResourcesParams = z.infer<typeof PaginateGroupResourcesParamsSchema>;
export type GroupResourcePaginationResult = z.infer<typeof GroupResourcePaginationResultSchema>;
export type GroupResourceDuplicate = z.infer<typeof GroupResourceDuplicateSchema>;
